import {
  DbHistoryRow,
  DbShipment,
  getSupabase,
  isSupabaseConfigured,
} from '../lib/supabase';
import { Shipment, ShipmentStatus, StatusEvent } from '../types/shipment';
import * as localStore from './shipmentStore';

const statusOrder: ShipmentStatus[] = ['arrived', 'inspection', 'clearance', 'release', 'delivered'];

export const getStatusOrder = () => statusOrder;

export const isCloudMode = () => isSupabaseConfigured();

const mapStatus = (value: string): ShipmentStatus =>
  statusOrder.includes(value as ShipmentStatus) ? (value as ShipmentStatus) : 'arrived';

const mapHistory = (rows: Array<{ status: string; at?: string; created_at?: string; note?: string | null }>): StatusEvent[] =>
  rows
    .map((row) => ({
      status: mapStatus(row.status),
      at: row.at || row.created_at || new Date().toISOString(),
      note: row.note ?? undefined,
    }))
    .sort((a, b) => statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status));

const mapShipment = (row: DbShipment & { client_phone?: string | null }, history: StatusEvent[] = []): Shipment => ({
  id: row.id,
  declarationNumber: row.declaration_number,
  clientEmail: row.client_email,
  clientName: row.client_name,
  company: row.company ?? undefined,
  clientCode: row.client_code ?? undefined,
  clientPhone: row.client_phone ?? undefined,
  cargoDescription: row.cargo_description,
  border: row.border,
  weightTons: row.weight_tons ?? undefined,
  status: mapStatus(row.status),
  history,
  updatedAt: row.updated_at,
});

const fetchHistory = async (shipmentId: string): Promise<StatusEvent[]> => {
  const supabase = getSupabase();
  if (!supabase) return [];

  const { data } = await supabase
    .from('shipment_history')
    .select('*')
    .eq('shipment_id', shipmentId)
    .order('created_at', { ascending: true });

  return mapHistory((data as DbHistoryRow[]) || []);
};

export async function getShipmentsForClient(email: string): Promise<Shipment[]> {
  if (!isSupabaseConfigured()) {
    return localStore.getShipmentsForClient(email);
  }

  const supabase = getSupabase()!;
  const { data, error } = await supabase
    .from('shipments')
    .select('*')
    .ilike('client_email', email)
    .order('updated_at', { ascending: false });

  if (error || !data) return [];

  const rows = data as DbShipment[];
  return Promise.all(
    rows.map(async (row) => mapShipment(row, await fetchHistory(row.id)))
  );
}

export async function getAllShipments(): Promise<Shipment[]> {
  if (!isSupabaseConfigured()) {
    return localStore.getAllShipments();
  }

  const supabase = getSupabase()!;
  const { data, error } = await supabase
    .from('shipments')
    .select('*')
    .order('updated_at', { ascending: false });

  if (error || !data) return [];

  const rows = data as DbShipment[];
  return Promise.all(
    rows.map(async (row) => mapShipment(row, await fetchHistory(row.id)))
  );
}

export async function getShipmentById(id: string): Promise<Shipment | null> {
  if (!isSupabaseConfigured()) {
    const all = localStore.getAllShipments();
    return all.find((s) => s.id === id) ?? null;
  }

  const supabase = getSupabase()!;
  const { data, error } = await supabase
    .from('shipments')
    .select('*')
    .eq('id', id)
    .single();

  if (error || !data) return null;
  const row = data as DbShipment;
  return mapShipment(row, await fetchHistory(row.id));
}

export async function getShipmentByDeclaration(declarationNumber: string): Promise<Shipment | null> {
  if (!isSupabaseConfigured()) {
    return localStore.getShipmentByDeclaration(declarationNumber);
  }

  const supabase = getSupabase()!;
  const { data, error } = await supabase.rpc('public_track_declaration', {
    decl_num: declarationNumber.trim(),
  });

  if (error || !data) return null;

  const payload = data as {
    id: string;
    declarationNumber: string;
    cargoDescription: string;
    border: string;
    status: string;
    updatedAt: string;
    history?: Array<{ status: string; at: string; note?: string | null }>;
  };

  const history = payload.history?.length
    ? mapHistory(payload.history)
    : await fetchHistory(payload.id);

  return {
    id: payload.id,
    declarationNumber: payload.declarationNumber,
    clientEmail: '',
    clientName: '',
    cargoDescription: payload.cargoDescription,
    border: payload.border,
    status: mapStatus(payload.status),
    history,
    updatedAt: payload.updatedAt,
  };
}

export async function updateShipmentStatus(
  id: string,
  status: ShipmentStatus,
  note?: string
): Promise<Shipment | null> {
  if (!isSupabaseConfigured()) {
    return localStore.updateShipmentStatus(id, status, note);
  }

  const supabase = getSupabase()!;
  const { data, error } = await supabase
    .from('shipments')
    .update({ status })
    .eq('id', id)
    .select('*')
    .single();

  if (error || !data) return null;

  if (note?.trim()) {
    const { data: histRows } = await supabase
      .from('shipment_history')
      .select('id')
      .eq('shipment_id', id)
      .eq('status', status)
      .order('created_at', { ascending: false })
      .limit(1);

    if (histRows?.[0]?.id) {
      await supabase
        .from('shipment_history')
        .update({ note: note.trim() })
        .eq('id', histRows[0].id);
    }
  }

  const row = data as DbShipment;
  return mapShipment(row, await fetchHistory(row.id));
}

export async function updateShipment(
  id: string,
  patch: Partial<{
    clientEmail: string;
    clientName: string;
    clientPhone: string;
    company: string;
    clientCode: string;
    cargoDescription: string;
    border: string;
    declarationNumber: string;
  }>
): Promise<Shipment | null> {
  if (!isSupabaseConfigured()) {
    return null;
  }

  const supabase = getSupabase()!;
  const payload: Record<string, unknown> = {};
  if (patch.clientEmail !== undefined) payload.client_email = patch.clientEmail.trim();
  if (patch.clientName !== undefined) payload.client_name = patch.clientName.trim();
  if (patch.clientPhone !== undefined) payload.client_phone = patch.clientPhone.trim() || null;
  if (patch.company !== undefined) payload.company = patch.company.trim() || null;
  if (patch.clientCode !== undefined) payload.client_code = patch.clientCode.trim() || null;
  if (patch.cargoDescription !== undefined) payload.cargo_description = patch.cargoDescription.trim();
  if (patch.border !== undefined) payload.border = patch.border.trim();
  if (patch.declarationNumber !== undefined) payload.declaration_number = patch.declarationNumber.trim();

  const { data, error } = await supabase
    .from('shipments')
    .update(payload)
    .eq('id', id)
    .select('*')
    .single();

  if (error || !data) return null;
  const row = data as DbShipment;
  return mapShipment(row, await fetchHistory(row.id));
}

export async function createShipment(
  input: Omit<Shipment, 'id' | 'history' | 'updatedAt' | 'status'> & { status?: ShipmentStatus }
): Promise<Shipment | null> {
  if (!isSupabaseConfigured()) {
    return localStore.createShipment(input);
  }

  const supabase = getSupabase()!;
  const { data, error } = await supabase
    .from('shipments')
    .insert({
      declaration_number: input.declarationNumber.trim(),
      client_email: input.clientEmail.trim(),
      client_name: input.clientName.trim(),
      company: input.company || null,
      client_code: input.clientCode?.trim() || null,
      client_phone: input.clientPhone?.trim() || null,
      cargo_description: input.cargoDescription.trim(),
      border: input.border.trim(),
      weight_tons: input.weightTons ?? null,
      status: input.status ?? 'arrived',
    })
    .select('*')
    .single();

  if (error || !data) return null;

  const row = data as DbShipment;
  return mapShipment(row, await fetchHistory(row.id));
}

export function ensureLocalSeedData() {
  if (!isSupabaseConfigured()) {
    localStore.ensureSeedData();
  }
}

export function formatPortalDate(iso: string, language: string): string {
  const locale = language === 'de' ? 'de-DE' : language === 'en' ? 'en-GB' : 'ar-SY';
  try {
    return new Date(iso).toLocaleString(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

export function buildWhatsAppStatusLink(
  phone: string | undefined,
  declarationNumber: string,
  statusLabel: string,
  note?: string
): string | null {
  const digits = (phone || '').replace(/\D/g, '');
  if (!digits) return null;
  const normalized = digits.startsWith('0') ? `963${digits.slice(1)}` : digits;
  const text = [
    `تحديث حالة البيان ${declarationNumber}`,
    `الحالة الحالية: ${statusLabel}`,
    note ? `ملاحظة: ${note}` : '',
    'شركة الشراع للتخليص الجمركي',
    'https://alshiraa-customs.com/portal',
  ]
    .filter(Boolean)
    .join('\n');
  return `https://wa.me/${normalized}?text=${encodeURIComponent(text)}`;
}
